import {
  TENDER_BUYER_TYPE_LABELS,
  TENDER_BUYER_TYPES,
  TENDER_NOTICE_TYPE_LABELS,
  TENDER_NOTICE_TYPES,
  type TenderWatchProfileDto,
  type UserDto,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { api } from '../../lib/api.js';
import {
  useDeleteWatchProfile,
  useSaveWatchProfile,
  useWatchProfiles,
  type WatchProfileInput,
} from './useSales.js';

export function WatchProfiles() {
  const { hasModule } = useAuth();
  const canEdit = hasModule('SALES', 'ADMIN');
  const { data: profiles = [], isLoading } = useWatchProfiles();
  const [editing, setEditing] = useState<TenderWatchProfileDto | 'new' | null>(null);

  if (isLoading) return <SkeletonPanel lines={5} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-ink-soft text-sm">
          Én profil per produktområde bestemmer hvilke kunngjøringer som er relevante.
        </p>
        {canEdit && (
          <Button size="sm" variant="primary" icon={<Plus size={14} aria-hidden />} onClick={() => setEditing('new')}>
            Ny profil
          </Button>
        )}
      </div>

      {profiles.length === 0 ? (
        <GlassPanel>
          <EmptyState title="Ingen overvåkningsprofiler ennå." />
        </GlassPanel>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {profiles.map((p) => (
            <ProfileCard key={p.id} profile={p} canEdit={canEdit} onEdit={() => setEditing(p)} />
          ))}
        </div>
      )}

      {editing && (
        <ProfileEditor
          profile={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ProfileCard({
  profile,
  canEdit,
  onEdit,
}: {
  profile: TenderWatchProfileDto;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const del = useDeleteWatchProfile();
  return (
    <GlassPanel className={profile.isActive ? '' : 'opacity-60'}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-ink text-sm font-medium">{profile.name}</h3>
          <p className="text-ink-soft text-xs">{profile.productArea}</p>
        </div>
        {canEdit && (
          <div className="flex shrink-0 gap-1">
            <Button size="sm" variant="ghost" icon={<Pencil size={13} aria-hidden />} onClick={onEdit}>
              Endre
            </Button>
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 size={13} aria-hidden />}
              loading={del.isPending}
              onClick={() => {
                if (confirm(`Slette profilen «${profile.name}»?`)) del.mutate(profile.id);
              }}
            >
              <span className="sr-only">Slett</span>
            </Button>
          </div>
        )}
      </div>
      <dl className="mt-2 space-y-1 text-xs">
        <Row label="CPV" value={profile.cpvCodes.join(', ') || '—'} />
        <Row label="Inkluder" value={profile.includeKeywords.join(', ') || '—'} />
        <Row label="Ekskluder" value={profile.excludeKeywords.join(', ') || '—'} />
        <Row label="Terskel" value={`${profile.minRelevance}/100`} />
        <Row label="Varsler" value={profile.notifyUsers.map((u) => u.name).join(', ') || '—'} />
      </dl>
    </GlassPanel>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-ink-soft w-20 shrink-0">{label}</dt>
      <dd className="text-ink min-w-0 break-words">{value}</dd>
    </div>
  );
}

function toList(value: string): string[] {
  return value
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function ProfileEditor({
  profile,
  onClose,
}: {
  profile: TenderWatchProfileDto | null;
  onClose: () => void;
}) {
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserDto[]>('/users'),
  });
  const save = useSaveWatchProfile(profile?.id);

  const [form, setForm] = useState({
    name: profile?.name ?? '',
    productArea: profile?.productArea ?? '',
    description: profile?.description ?? '',
    cpvCodes: (profile?.cpvCodes ?? []).join(', '),
    includeKeywords: (profile?.includeKeywords ?? []).join(', '),
    excludeKeywords: (profile?.excludeKeywords ?? []).join(', '),
    buyerTypes: profile?.buyerTypes ?? [],
    noticeTypes: profile?.noticeTypes ?? ([] as string[]),
    notifyUserIds: profile?.notifyUserIds ?? [],
    minRelevance: profile?.minRelevance ?? 50,
    isActive: profile?.isActive ?? true,
  });

  function toggle<T extends string>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  function submit() {
    const body: WatchProfileInput = {
      name: form.name.trim(),
      productArea: form.productArea.trim(),
      description: form.description.trim() || null,
      cpvCodes: toList(form.cpvCodes),
      includeKeywords: toList(form.includeKeywords),
      excludeKeywords: toList(form.excludeKeywords),
      buyerTypes: form.buyerTypes,
      noticeTypes: form.noticeTypes,
      notifyUserIds: form.notifyUserIds,
      minRelevance: Number(form.minRelevance),
      isActive: form.isActive,
    };
    save.mutate(body, { onSuccess: onClose });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <GlassPanel className="relative z-10 max-h-[90dvh] w-full max-w-lg overflow-y-auto">
        <h3 className="font-display text-ink mb-3 text-lg">
          {profile ? 'Endre overvåkningsprofil' : 'Ny overvåkningsprofil'}
        </h3>
        <div className="space-y-3">
          <Field label="Navn">
            <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Produktområde">
            <input
              className={inputCls}
              value={form.productArea}
              onChange={(e) => setForm({ ...form, productArea: e.target.value })}
            />
          </Field>
          <Field label="Beskrivelse">
            <textarea
              className={inputCls}
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Field>
          <Field label="CPV-koder (kommaseparert)">
            <input
              className={inputCls}
              value={form.cpvCodes}
              onChange={(e) => setForm({ ...form, cpvCodes: e.target.value })}
              placeholder="72000000, 48000000"
            />
          </Field>
          <Field label="Nøkkelord — inkluder (kommaseparert)">
            <input
              className={inputCls}
              value={form.includeKeywords}
              onChange={(e) => setForm({ ...form, includeKeywords: e.target.value })}
            />
          </Field>
          <Field label="Nøkkelord — ekskluder (kommaseparert)">
            <input
              className={inputCls}
              value={form.excludeKeywords}
              onChange={(e) => setForm({ ...form, excludeKeywords: e.target.value })}
              placeholder="brøyting, renhold"
            />
          </Field>
          <Field label="Oppdragsgivertyper">
            <div className="flex flex-wrap gap-2">
              {TENDER_BUYER_TYPES.map((b) => (
                <Chip
                  key={b}
                  active={form.buyerTypes.includes(b)}
                  onClick={() => setForm({ ...form, buyerTypes: toggle(form.buyerTypes, b) })}
                >
                  {TENDER_BUYER_TYPE_LABELS[b]}
                </Chip>
              ))}
            </div>
          </Field>
          <Field label="Kunngjøringstyper (tom = alle)">
            <div className="flex flex-wrap gap-2">
              {TENDER_NOTICE_TYPES.map((t) => (
                <Chip
                  key={t}
                  active={form.noticeTypes.includes(t)}
                  onClick={() => setForm({ ...form, noticeTypes: toggle(form.noticeTypes, t) })}
                >
                  {TENDER_NOTICE_TYPE_LABELS[t]}
                </Chip>
              ))}
            </div>
          </Field>
          <Field label="Varsle disse">
            <div className="flex flex-wrap gap-2">
              {users.map((u) => (
                <Chip
                  key={u.id}
                  active={form.notifyUserIds.includes(u.id)}
                  onClick={() => setForm({ ...form, notifyUserIds: toggle(form.notifyUserIds, u.id) })}
                >
                  {u.name}
                </Chip>
              ))}
            </div>
          </Field>
          <div className="flex items-center gap-4">
            <Field label="Varslingsterskel">
              <input
                type="number"
                min={0}
                max={100}
                className={inputCls}
                value={form.minRelevance}
                onChange={(e) => setForm({ ...form, minRelevance: Number(e.target.value) })}
              />
            </Field>
            <label className="text-ink mt-5 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Aktiv
            </label>
          </div>

          {save.isError && <p className="text-danger text-xs">{(save.error as Error).message}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}>
              Avbryt
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={submit}>
              Lagre
            </Button>
          </div>
        </div>
      </GlassPanel>
    </div>
  );
}

const inputCls = 'border-hairline bg-base/40 text-ink w-full rounded-lg border px-3 py-2 text-sm';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="label-micro text-ink-soft mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'bg-accent/15 text-accent border-accent/30 rounded-full border px-2.5 py-1 text-xs'
          : 'border-hairline text-ink-soft hover:text-ink rounded-full border px-2.5 py-1 text-xs'
      }
    >
      {children}
    </button>
  );
}
